/**
 * Typography Configuration for Material-UI Theme
 *
 * Defines typography styles including font families, sizes, weights, and line heights.
 * This configuration ensures consistent typography across the application.
 *
 * @param {Object} theme - Theme customization object
 * @param {string} theme.heading - Heading text color
 * @param {string} theme.textDark - Dark text color
 * @param {string} theme.darkTextPrimary - Primary text color
 * @param {string} theme.darkTextSecondary - Secondary text color
 * @param {Object} theme.customization - Custom theme options
 * @param {string} theme.customization.fontFamily - Font family configuration
 *
 * @returns {Object} Material-UI typography configuration
 */
export default function themeTypography(theme) {
  return {
    // ==============================|| FONT FAMILY ||============================== //
    fontFamily:
      theme?.customization?.fontFamily ||
      '"IBM Plex Sans", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',

    // ==============================|| HEADINGS ||============================== //
    h1: {
      fontSize: "2.125rem", // 34px
      fontWeight: 700,
      lineHeight: 1.2,
      color: theme.heading,
      letterSpacing: "-0.01562em",
    },
    h2: {
      fontSize: "1.5rem", // 24px
      fontWeight: 700,
      lineHeight: 1.3,
      color: theme.heading,
      letterSpacing: "-0.00833em",
    },
    h3: {
      fontSize: "1.25rem", // 20px
      fontWeight: 600,
      lineHeight: 1.4,
      color: theme.heading,
      letterSpacing: "0em",
    },
    h4: {
      fontSize: "1rem", // 16px
      fontWeight: 600,
      lineHeight: 1.4,
      color: theme.heading,
      letterSpacing: "0.00735em",
    },
    h5: {
      fontSize: "0.875rem", // 14px
      fontWeight: 500,
      lineHeight: 1.5,
      color: theme.heading,
      letterSpacing: "0em",
    },
    h6: {
      fontSize: "0.75rem", // 12px
      fontWeight: 500,
      lineHeight: 1.6,
      color: theme.heading,
      letterSpacing: "0.0075em",
    },

    // ==============================|| SUBTITLES ||============================== //
    subtitle1: {
      fontSize: "0.875rem", // 14px
      fontWeight: 500,
      lineHeight: 1.5,
      color: theme.textDark,
      letterSpacing: "0.00938em",
    },
    subtitle2: {
      fontSize: "0.75rem", // 12px
      fontWeight: 400,
      lineHeight: 1.6,
      color: theme.darkTextSecondary,
      letterSpacing: "0.00714em",
    },

    // ==============================|| BODY TEXT ||============================== //
    body1: {
      fontSize: "0.875rem", // 14px
      fontWeight: 400,
      lineHeight: 1.5,
      color: theme.darkTextPrimary,
      letterSpacing: "0.00938em",
    },
    body2: {
      fontSize: "0.75rem", // 12px
      fontWeight: 400,
      lineHeight: 1.6,
      color: theme.darkTextPrimary,
      letterSpacing: "0.01071em",
    },

    // ==============================|| CAPTION & OVERLINE ||============================== //
    caption: {
      fontSize: "0.75rem", // 12px
      fontWeight: 400,
      lineHeight: 1.6,
      color: theme.darkTextSecondary,
      letterSpacing: "0.03333em",
    },
    overline: {
      fontSize: "0.75rem", // 12px
      fontWeight: 400,
      lineHeight: 2.66,
      color: theme.darkTextSecondary,
      letterSpacing: "0.08333em",
      textTransform: "uppercase",
    },

    // ==============================|| BUTTON TEXT ||============================== //
    button: {
      fontSize: "0.875rem", // 14px
      fontWeight: 500,
      lineHeight: 1.75,
      color: theme.darkTextPrimary,
      letterSpacing: "0.02857em",
      textTransform: "none",
    },

    // ==============================|| CUSTOM TYPOGRAPHY ||============================== //
    // Additional custom typography variants
    display1: {
      fontSize: "3rem", // 48px
      fontWeight: 700,
      lineHeight: 1.2,
      color: theme.heading,
      letterSpacing: "-0.02em",
    },
    display2: {
      fontSize: "2.5rem", // 40px
      fontWeight: 600,
      lineHeight: 1.3,
      color: theme.heading,
      letterSpacing: "-0.01em",
    },
    lead: {
      fontSize: "1.125rem", // 18px
      fontWeight: 400,
      lineHeight: 1.6,
      color: theme.darkTextPrimary,
      letterSpacing: "0em",
    },
    small: {
      fontSize: "0.6875rem", // 11px
      fontWeight: 400,
      lineHeight: 1.6,
      color: theme.darkTextSecondary,
      letterSpacing: "0.00909em",
    },
  };
}
